import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, Edit, Check, X, Users, Building, ChevronRight, BookOpen } from 'lucide-react';
import { schoolsApi, usersApi, coursesApi } from '../../lib/api';
import { toast } from 'sonner';

export const SchoolsManagement = () => {
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showBulkUsersDialog, setShowBulkUsersDialog] = useState(false);
  const [showBulkCoursesDialog, setShowBulkCoursesDialog] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '' });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [schoolUsers, setSchoolUsers] = useState([]);
  const [schoolCourses, setSchoolCourses] = useState([]);
  const [expandedSchool, setExpandedSchool] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolsData, usersData, coursesData] = await Promise.all([
        schoolsApi.getAll(),
        usersApi.getAll(),
        coursesApi.getAll()
      ]);
      setSchools(schoolsData);
      setUsers(usersData);
      setCourses(coursesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolDetails = async (schoolId) => {
    try {
      const [usersData, coursesData] = await Promise.all([
        schoolsApi.getUsers(schoolId),
        schoolsApi.getCourses(schoolId)
      ]);
      setSchoolUsers(usersData);
      setSchoolCourses(coursesData);
    } catch (error) {
      toast.error('Failed to load school details');
    }
  };

  const toggleSchoolExpand = async (school) => {
    if (expandedSchool === school.school_id) {
      setExpandedSchool(null);
    } else {
      setExpandedSchool(school.school_id);
      await fetchSchoolDetails(school.school_id);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingSchool) {
        await schoolsApi.update(editingSchool.school_id, formData);
        toast.success('School updated');
      } else {
        await schoolsApi.create(formData);
        toast.success('School created');
      }
      setShowDialog(false);
      setEditingSchool(null);
      setFormData({ name: '', address: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (schoolId) => {
    if (!window.confirm('Are you sure you want to delete this school?')) return;
    try {
      await schoolsApi.delete(schoolId);
      toast.success('School deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkAssignUsers = async () => {
    if (!selectedSchool || selectedUserIds.length === 0) return;
    try {
      const result = await schoolsApi.bulkAssignUsers(selectedSchool.school_id, selectedUserIds);
      toast.success(result.message);
      setShowBulkUsersDialog(false);
      setSelectedUserIds([]);
      fetchData();
      if (expandedSchool === selectedSchool.school_id) {
        fetchSchoolDetails(selectedSchool.school_id);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkAssignCourses = async () => {
    if (!selectedSchool || selectedCourseIds.length === 0) return;
    try {
      const result = await schoolsApi.bulkAssignCourses(selectedSchool.school_id, selectedCourseIds);
      toast.success(result.message);
      setShowBulkCoursesDialog(false);
      setSelectedCourseIds([]);
      if (expandedSchool === selectedSchool.school_id) {
        fetchSchoolDetails(selectedSchool.school_id);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveUser = async (schoolId, userId) => {
    try {
      await schoolsApi.removeUser(schoolId, userId);
      toast.success('User removed from school');
      fetchSchoolDetails(schoolId);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEdit = (school) => {
    setEditingSchool(school);
    setFormData({ name: school.name, address: school.address || '' });
    setShowDialog(true);
  };

  const openBulkUsers = (school) => {
    setSelectedSchool(school);
    setSelectedUserIds([]);
    setShowBulkUsersDialog(true);
  };

  const openBulkCourses = (school) => {
    setSelectedSchool(school);
    setSelectedCourseIds([]);
    setShowBulkCoursesDialog(true);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  // Filter users not already in the selected school
  const availableUsers = users.filter(u => u.school_id !== selectedSchool?.school_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Schools Management</h1>
        <Button onClick={() => { setEditingSchool(null); setFormData({ name: '', address: '' }); setShowDialog(true); }} data-testid="add-school-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add School
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-lg" />)}
        </div>
      ) : schools.length === 0 ? (
        <div className="empty-state py-12 rounded-xl bg-card border border-border">
          <Building className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No schools yet</h3>
          <p className="text-muted-foreground">Add your first school to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {schools.map((school) => (
            <div key={school.school_id} className="rounded-lg bg-card border border-border overflow-hidden" data-testid={`school-${school.school_id}`}>
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggleSchoolExpand(school)}>
                  <ChevronRight className={`w-5 h-5 transition-transform ${expandedSchool === school.school_id ? 'rotate-90' : ''}`} />
                  <div>
                    <h3 className="font-semibold">{school.name}</h3>
                    <p className="text-sm text-muted-foreground">{school.address || 'No address'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openBulkUsers(school)} data-testid={`bulk-users-${school.school_id}`}>
                    <Users className="w-4 h-4 mr-1" />
                    Assign Users
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openBulkCourses(school)} data-testid={`bulk-courses-${school.school_id}`}>
                    <BookOpen className="w-4 h-4 mr-1" />
                    Assign Courses
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(school)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(school.school_id)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {expandedSchool === school.school_id && (
                <div className="border-t border-border p-4 bg-muted/30">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Assigned Users ({schoolUsers.length})
                      </h4>
                      {schoolUsers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No users assigned</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {schoolUsers.map(user => (
                            <div key={user.user_id} className="flex items-center justify-between p-2 rounded bg-background">
                              <div>
                                <span className="text-sm font-medium">{user.name}</span>
                                <span className="text-xs text-muted-foreground ml-2">{user.role}</span>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveUser(school.school_id, user.user_id)} className="text-destructive h-7">
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        Available Courses ({schoolCourses.length})
                      </h4>
                      {schoolCourses.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No courses assigned</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {schoolCourses.map(course => (
                            <div key={course.course_id} className="p-2 rounded bg-background">
                              <span className="text-sm font-medium">{course.title}</span>
                              <Badge variant="outline" className="ml-2 text-xs">{course.language || course.category?.replace('_', ' ') || 'Course'}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit School Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSchool ? 'Edit School' : 'Add School'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>School Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter school name" data-testid="school-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Enter address" data-testid="school-address-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-school-btn">{editingSchool ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Users Dialog */}
      <Dialog open={showBulkUsersDialog} onOpenChange={setShowBulkUsersDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Users to {selectedSchool?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select users to assign to this school. Selected: {selectedUserIds.length}
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
              {availableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">All users are already assigned to schools</p>
              ) : (
                availableUsers.map(user => (
                  <div
                    key={user.user_id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUserIds.includes(user.user_id)
                        ? 'bg-primary/10 border border-primary'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    onClick={() => toggleUserSelection(user.user_id)}
                    data-testid={`select-user-${user.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{user.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">{user.email}</span>
                      </div>
                      {selectedUserIds.includes(user.user_id) && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUsersDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignUsers} disabled={selectedUserIds.length === 0} data-testid="confirm-bulk-users">
              Assign {selectedUserIds.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Courses Dialog */}
      <Dialog open={showBulkCoursesDialog} onOpenChange={setShowBulkCoursesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Courses to {selectedSchool?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select courses to make available to all users in this school. Selected: {selectedCourseIds.length}
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
              {courses.map(course => (
                <div
                  key={course.course_id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedCourseIds.includes(course.course_id)
                      ? 'bg-primary/10 border border-primary'
                      : 'bg-muted/50 hover:bg-muted'
                  }`}
                  onClick={() => toggleCourseSelection(course.course_id)}
                  data-testid={`select-course-${course.course_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{course.title}</span>
                      <Badge variant="outline" className="ml-2">{course.language || course.category?.replace('_', ' ') || 'Course'}</Badge>
                    </div>
                    {selectedCourseIds.includes(course.course_id) && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkCoursesDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignCourses} disabled={selectedCourseIds.length === 0} data-testid="confirm-bulk-courses">
              Assign {selectedCourseIds.length} Courses
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Users Management
